'use client';
import { ProductCardContext } from '.';
import { cva, VariantProps } from 'class-variance-authority';
import { ComponentProps, useContext } from 'react';
import { Badge } from '@/shared/ui/badge';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';
import { ProductCardProps } from './types';

const productCardAvailabilityVariants = cva(null, {
    variants: {
        variant: {
            badge: 'product-card__availability-badge',
            strip: 'product-card__availability-strip',
        },
    },
    defaultVariants: {
        variant: 'badge',
    },
});

type Props = ComponentProps<typeof Layout> &
    Partial<Pick<ProductCardProps, 'availability'>> &
    VariantProps<typeof productCardAvailabilityVariants>;
export const ProductCardAvailability = ({
    availability,
    variant = 'badge',
    media,
    className: _,
    ...props
}: Props) => {
    const context = useContext(ProductCardContext);

    const finalAvailability = availability ?? context.availability;

    if (
        !finalAvailability ||
        (finalAvailability.isVisibleInImage && variant === 'strip') ||
        (!finalAvailability.isVisibleInImage && variant === 'badge')
    ) {
        return null;
    }
    const variantClassName = productCardAvailabilityVariants({ variant });

    if (variant === 'strip') {
        return (
            <Layout
                element="div"
                className={variantClassName}
                data-testid="product-card-availability"
                media={{
                    initial: {},
                    ...media,
                }}
                {...props}
            >
                <Icon name="clock" />
                <span>{`${finalAvailability.fromTime} - ${finalAvailability.toTime}`}</span>
            </Layout>
        );
    }

    return (
        <Badge variant="tertiary" data-testid="product-card-availability" className={variantClassName}>
            <Icon name="clock" />
            {`${finalAvailability.fromTime} - ${finalAvailability.toTime}`}
        </Badge>
    );
};
